import { UseQueryOptions, UseQueryResult, useQueries } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { AssembledSeries } from '@/api/liveSplitQuery/assembledSeries';
import { placeholderCache, placeholderCacheKey } from '@/api/liveSplitQuery/placeholderCache';
import {
    LiveSplitEitherSpotterOptions,
    LiveSplitManySpotterOptions,
    LiveSplitOneSpotterOptions,
    LiveSplitQueryOptions,
} from '@/api/liveSplitQuery/types';
import {
    SpotterWindowLayout,
    buildWindowQueries,
    combineSpotterWindows,
} from '@/api/liveSplitQuery/windowedSpotterQueries';
import { CombinedQueriesResult, CombinedQueryOrQueriesResult, CombinedQueryResult } from '@/common/react-query';
import { useWavelink } from '@/components/providers/WavelinkProvider';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useViewToken } from '@/context/ViewTokenContext';
import { useStableValue } from '@/hooks/useStableValue';
import { chunkDateRange, settledChunkHours, splitLiveRange, startOfHourUTC } from '@/utils';

export type {
    FetchRangeParams,
    LiveSplitEitherSpotterOptions,
    LiveSplitManySpotterOptions,
    LiveSplitOneSpotterOptions,
    LiveSplitQueryOptions,
} from '@/api/liveSplitQuery/types';

/**
 * Runs a date-ranged query as a series of settled windows whose cache keys stay put, plus a small live
 * window that is the only thing ever refetched.
 *
 * This exists because a live range tracks `now`, which DateRangeContext advances every 5 minutes. Keying
 * on that directly means a brand new cache entry, and a full refetch of the whole window, twelve times an
 * hour. `splitLiveRange` owns where the windows are cut and why.
 *
 * Ask for the range you want. The windows fetched are widened to the hour so their keys hold still, and
 * the series handed back is clipped to the range asked for, out to however far the live half overshoots.
 * A caller therefore never has to know where the cuts fall to keep its own bounds cacheable.
 *
 * The settled part is cut again into chunks by `chunkDateRange`, because a settled window is as long as
 * the caller asked for and one request that wide is the thing that times out. Chunks sit on an absolute
 * grid, so a chunk fetched for one range is a cache hit for the next range overlapping it, and the hour
 * rollover moves only the last of them.
 *
 * Every window is an ordinary keyed query, so react-query dedupes them across every hook asking for the
 * same range and no cache mutation is involved.
 *
 * Takes one spotter or a list of them, and answers in kind: `spotterId` gets that spotter's series,
 * `spotterIds` gets one series per spotter in the order asked for. The third overload is for a wrapper
 * passing its own destructured {@link SpotterIdOrIds} straight through, which is the only way to reach
 * both parameters at once; such a wrapper narrows the answer again with its own overloads.
 */
export function useLiveSplitQuery<TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>(
    options: LiveSplitOneSpotterOptions<TItem, TTimestampKey>
): CombinedQueryResult<TItem[]>;
export function useLiveSplitQuery<TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>(
    options: LiveSplitManySpotterOptions<TItem, TTimestampKey>
): CombinedQueriesResult<TItem[]>;
export function useLiveSplitQuery<TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>(
    options: LiveSplitEitherSpotterOptions<TItem, TTimestampKey>
): CombinedQueryOrQueriesResult<TItem[]>;

export function useLiveSplitQuery<TItem extends Record<TTimestampKey, string>, TTimestampKey extends string>({
    queryKeyPrefix,
    spotterId,
    spotterIds,
    dateRange,
    keyOptions,
    fetchRange,
    apiDateBounds,
    maxChunkHours,
    timestampKey,
    sortOrder,
    responseSortOrder = sortOrder,
    config: { enabled = true, ...config } = {},
}: LiveSplitQueryOptions<TItem, TTimestampKey>): CombinedQueryOrQueriesResult<TItem[]> {
    type SubQueryOptions = UseQueryOptions<TItem[]>;

    /*
     * The two spotter parameters are one list from here down. `spotterId` wins where both carry a value,
     * and is kept only to unwrap the answer again at the bottom.
     */
    const spotterIdList = useMemo(() => (spotterId ? [spotterId] : spotterIds), [spotterId, spotterIds]);

    /*
     * Stabilize parameter identity so we don't lose our memos if callers construct anonymous argument objects.
     */
    const stableSpotterIds = useStableValue(spotterIdList);
    const stableKeyOptions = useStableValue(keyOptions);
    const stableConfig = useStableValue(config);

    /*
     * Also callers construct anonymous fetchRange functions arguments but we can't stabilize a function, but also
     * we don't need to react to changes, so just stash in a ref.
     */
    const fetchRangeRef = useRef(fetchRange);
    fetchRangeRef.current = fetchRange;

    const { loading: loadingToken, viewToken } = useViewToken();
    const { isQueryCovered } = useWavelink();

    // This query is called more often but we recompute the split when the hour rolls over
    const currentHourMs = startOfHourUTC(new Date()).getTime();
    const startMs = dateRange?.startDate.getTime();
    const endMs = dateRange?.endDate.getTime();
    const split = useMemo(
        () =>
            isNil(startMs) || isNil(endMs)
                ? {}
                : splitLiveRange({ startDate: new Date(startMs), endDate: new Date(endMs) }, new Date(currentHourMs)),
        [startMs, endMs, currentHourMs]
    );

    /*
     * The memo above still recomputes every 5 minutes for a live range, because that is how often
     * DateRangeContext advances an `endDate` that tracks `now`. It recomputes to the same two windows,
     * since both bounds are snapped to the hour, so hold the identity until the values actually change.
     * Otherwise `data` re-concatenates and every chart on the page redraws twelve times an hour, which is
     * one of the things this hook exists to remove.
     */
    const { settledDateRange, liveDateRange } = useStableValue(split);

    const settledChunks = useMemo(
        () =>
            settledDateRange
                ? chunkDateRange(settledDateRange, settledChunkHours(settledDateRange, maxChunkHours))
                : [],
        [settledDateRange, maxChunkHours]
    );

    /*
     * How far the windows actually reach, which is past the caller's `endDate` while the live half
     * overshoots to the next hour. `splitLiveRange` holds this still for the hour, while the caller's own
     * bounds advance every five minutes for as long as they track `now`.
     *
     * This is the far edge of what the caller is handed, rather than its own `endDate`: the overshoot is
     * where an observation newer than an up-to-five-minute-stale `now` shows up, and reporting it is what
     * lets a live axis reach its last datapoint.
     */
    const windowEndMs = (liveDateRange ?? settledDateRange)?.endDate.getTime();

    const isEnabled = Boolean(enabled && stableSpotterIds?.length && !isNil(dateRange) && !loadingToken);

    const isPublic = Boolean(viewToken);

    /*
     * The series half of a `PlaceholderCacheKey`, which names every input that changes what a window's
     * rows are or what order they come in. See `PlaceholderCacheKey` for what a key has to cover.
     *
     * Every element is already reference-stable, so a memo holds the array's identity for as long as the
     * key itself is unchanged.
     */
    const seriesKey = useMemo(
        () => [queryKeyPrefix, stableKeyOptions, isPublic, sortOrder, timestampKey],
        [queryKeyPrefix, stableKeyOptions, isPublic, sortOrder, timestampKey]
    );

    /** One key per spotter, in `spotterIds` order. */
    const cacheKeys = useMemo(
        () => (stableSpotterIds ?? []).map((id) => placeholderCacheKey(id, seriesKey)),
        [seriesKey, stableSpotterIds]
    );

    /** The series handed out last, reused so an observer with nothing new sees an unchanged result. */
    const seriesRef = useRef<AssembledSeries<TItem>>();

    /** Where each spotter's windows sit in `queries`, and the bounds their series is read under. */
    const layout = useMemo<SpotterWindowLayout<TTimestampKey>>(
        () => ({
            spotterIds: stableSpotterIds,
            settledChunks,
            liveDateRange,
            cacheKeys,
            startMs,
            endMs,
            windowEndMs,
            timestampKey,
            sortOrder,
        }),
        [
            stableSpotterIds,
            settledChunks,
            liveDateRange,
            cacheKeys,
            startMs,
            endMs,
            windowEndMs,
            timestampKey,
            sortOrder,
        ]
    );

    /* Built from `layout`, so `combineSpotterWindows` slices these results under the layout they were fetched under. */
    const queries = useMemo<SubQueryOptions[]>(
        () =>
            buildWindowQueries({
                layout,
                queryKeyPrefix,
                keyOptions: stableKeyOptions,
                viewToken,
                isPublic,
                isQueryCovered,
                isEnabled,
                apiDateBounds,
                responseSortOrder,
                config: stableConfig,
                fetchRangeRef,
            }),
        [
            layout,
            viewToken,
            isPublic,
            stableKeyOptions,
            isQueryCovered,
            queryKeyPrefix,
            isEnabled,
            stableConfig,
            apiDateBounds,
            responseSortOrder,
        ]
    );

    /*
     * Rebuilt whenever `layout` changes, which is what pairs a set of results with the layout that slices
     * it. The observer reuses what it combined last until either its own committed results change or this
     * callback's identity does, so the callback has to change whenever a result moves from one spotter to
     * another.
     *
     * `layout` alone covers that, because `buildWindowQueries` takes the spotter list, the settled chunks
     * and the live window only from `layout`: the list is `spotterIds.length * (settledChunks.length + 1)`
     * entries in `layout` order. Every other parameter it reads shapes what a window asks for, not which
     * spotter owns it. `windowedSpotterQueries.test.ts` locks that down under "keeps the query list shaped
     * by the layout alone".
     */
    const combinePerSpotter = useCallback(
        (results: UseQueryResult<TItem[]>[]) => {
            const combined = combineSpotterWindows(results, layout, seriesRef.current);
            /*
             * Written from inside `combine`, which react-query calls during render. A render React then
             * discards leaves this holding a series that was never committed. `nextAssembledSeries` only
             * reuses what it is given on equality, so the next combine still hands out correct data; the
             * cost of a discarded render is one lost identity reuse, and one extra redraw with it.
             */
            seriesRef.current = combined.series;

            return combined;
        },
        [layout]
    );

    const { status, series, newestTimestamp } = useQueries({ queries, combine: combinePerSpotter });

    /*
     * Unwrapped outside the observer, so the series never passes through structural sharing.
     *
     * An unresolved spotter list answers `undefined` rather than `[]`, because `spotterIds: []` really is
     * successfully empty and a caller that passed `spotterId: null` can do nothing with an empty list.
     */
    const queryResult = useMemo<CombinedQueryOrQueriesResult<TItem[]>>(
        () => ({
            ...status,
            data: stableSpotterIds ? (spotterId ? series.data[0] : series.data) : undefined,
        }),
        [status, series, spotterId, stableSpotterIds]
    );

    /*
     * Remember what was assembled so a later range can show it while its own windows are in flight. Only
     * freshly assembled data is stored: storing what was carried forward would keep re-describing it as
     * covering whatever range it was last shown under.
     */
    useEffect(() => {
        cacheKeys.forEach((cacheKey, index) => {
            const freshlyAssembled = series.windows[index];
            if (freshlyAssembled) {
                placeholderCache.remember(cacheKey, freshlyAssembled);
            }
        });
    }, [series, cacheKeys]);

    /*
     * Report the newest datum so DateRangeContext can carry a live `endDate` past a `now` that is up to
     * five minutes stale, and the charts' x axis reaches the last data point rather than stopping short of it.
     */
    const { isMutable, setMaxObservedTimestamp } = useDateRangeContext();

    useEffect(() => {
        // Nothing to report to a fixed range, and nothing to report with no provider at all: both are immutable.
        if (!isMutable || !newestTimestamp) {
            return;
        }
        setMaxObservedTimestamp(new Date(newestTimestamp).toISOString());
    }, [isMutable, newestTimestamp, setMaxObservedTimestamp]);

    return queryResult;
}
